import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
export interface INote {
  id: string;
  type: NoteTypeEnum;
  amount: number;
  profileId: string;
  month: number;
  year: number;
  ref: string;
  invoiceId: string;
  connectionProfileIds: number[];
  comment: string;
  createdDate: string;
  createdBy: string;
  updatedBy?: string;
}
export enum AccountyEnumType {
  CLIENT,
  VENDOR,
}
export enum NoteTypeEnum {
  CREDIT = "CREDIT_NOTE",
  DEBIT = "DEBIT_NOTE",
}
export const exportNote = async (params: Params<INote> = {}): Promise<void> => {
  try {
    const response = await axiosInstance.get("/notes/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "Note.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching Note:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchNotes(
  params: Params<INote>
): Promise<IPageable<INote>> {
  try {
    const response = await axiosInstance.get("/notes", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}

export async function fetchNote(id: INote["id"]): Promise<INote> {
  try {
    const response = await axiosInstance.get(`/notes/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting routing-plan/${id}.`
    );
  }
}

export const addNoteApi = async (data: Partial<INote>) => {
  try {
    const response = await axiosInstance.post("/notes", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the notes."
    );
  }
};

export const notePdfApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/notes/${id}/pdf`, {
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "application/pdf" });

    saveAs(blob, `Note-${id}.pdf`);

    return response.data;
  } catch (error: any) {
    console.error(
      "Error fetching the PDF:",
      error.response?.status,
      error.response?.data
    );
  }
};

export const useNoteQuery = (
  params: Params<INote>
): UseQueryResult<IPageable<INote>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["note", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchNotes(effectiveParams),
  });
};